import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Table, Drawer, SearchBar, BadgePill, EmptyState } from '@/components/campus';
import {
  Building2,
  Calendar,
  Users,
  QrCode,
  MoreHorizontal,
  Eye,
  Loader2,
  GraduationCap,
  Globe,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  UserCheck,
  AlertTriangle,
  Crown,
  Ban,
  RefreshCw,
  UserPlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Org type colors
const orgTypeConfig = {
  fraternity: { label: 'Fraternity', variant: 'primary' },
  sorority: { label: 'Sorority', variant: 'success' },
  club: { label: 'Club', variant: 'warning' },
  promoter: { label: 'Promoter', variant: 'error' },
  organization: { label: 'Organization', variant: 'default' },
};

// ==================== CREATE/EDIT DIALOG ====================
const OrgFormDialog = ({ isOpen, onClose, org, campuses, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    organization_type: 'organization',
    campus_id: '',
    description: '',
    admin_email: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (org) {
      setFormData({
        name: org.name || '',
        organization_type: org.type || 'organization',
        campus_id: org.campus_id || '',
        description: org.description || '',
        admin_email: org.admin_email || '',
      });
    } else {
      setFormData({
        name: '',
        organization_type: 'organization',
        campus_id: '',
        description: '',
        admin_email: '',
      });
    }
  }, [org, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData, org?.id);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-graphite border-white/[0.08] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-cloud-white">
            {org ? 'Edit Organization' : 'Add Organization'}
          </DialogTitle>
          <DialogDescription className="text-soft-silver">
            {org ? 'Update organization details and administrator' : 'Create a new organization on the platform'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-soft-silver">Organization Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter organization name"
              className="bg-obsidian border-white/[0.08]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">Type</Label>
            <Select
              value={formData.organization_type}
              onValueChange={(v) => setFormData({ ...formData, organization_type: v })}
            >
              <SelectTrigger className="bg-obsidian border-white/[0.08]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-graphite border-white/[0.08]">
                <SelectItem value="organization">Organization</SelectItem>
                <SelectItem value="fraternity">Fraternity</SelectItem>
                <SelectItem value="sorority">Sorority</SelectItem>
                <SelectItem value="club">Club</SelectItem>
                <SelectItem value="promoter">Promoter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">Campus</Label>
            <Select
              value={formData.campus_id || 'none'}
              onValueChange={(v) => setFormData({ ...formData, campus_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger className="bg-obsidian border-white/[0.08]">
                <SelectValue placeholder="Select a campus" />
              </SelectTrigger>
              <SelectContent className="bg-graphite border-white/[0.08] max-h-60">
                <SelectItem value="none">No Campus</SelectItem>
                {campuses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
              className="bg-obsidian border-white/[0.08] min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-soft-silver flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              Administrator Email
            </Label>
            <Input
              type="email"
              value={formData.admin_email}
              onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
              placeholder="admin@example.com"
              className="bg-obsidian border-white/[0.08]"
            />
            <p className="text-xs text-muted-text">
              The user with this email will become the organization's administrator (owner)
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-electric-ice-blue text-obsidian hover:bg-electric-ice-blue/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {org ? 'Update' : 'Create'} Organization
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ==================== DELETE CONFIRMATION DIALOG ====================
const DeleteConfirmDialog = ({ isOpen, onClose, org, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm(org.id);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  if (!org) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-graphite border-white/[0.08] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-cloud-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-coral-red" />
            Delete Organization
          </DialogTitle>
          <DialogDescription className="text-soft-silver">
            Are you sure you want to delete <span className="text-cloud-white font-semibold">{org.name}</span>? 
            This action cannot be undone. All members will be removed.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete} 
            disabled={deleting}
            className="bg-coral-red hover:bg-coral-red/90"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== ORG DETAIL DRAWER ====================
const OrgDetailDrawer = ({ org, isOpen, onClose }) => {
  if (!org) return null;

  const typeConfig = orgTypeConfig[org.type] || orgTypeConfig.organization;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Organization Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-cool-mint/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-cool-mint" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-cloud-white">{org.name}</h3>
            <div className="flex items-center gap-2 mt-2">
              <BadgePill variant={typeConfig.variant}>{typeConfig.label}</BadgePill>
              {org.campus_name && (
                <BadgePill variant="default">
                  <GraduationCap className="w-3 h-3 mr-1" />
                  {org.campus_name}
                </BadgePill>
              )}
            </div>
          </div>
        </div>

        {/* Admin Info */}
        <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
          <p className="text-sm text-muted-text mb-2">Administrator</p>
          {org.admin_email ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-electric-ice-blue/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-electric-ice-blue" />
              </div>
              <div>
                <p className="text-cloud-white font-medium">{org.admin_name || 'Administrator'}</p>
                <p className="text-sm text-soft-silver">{org.admin_email}</p>
              </div>
              <BadgePill variant="success" className="ml-auto">{org.admin_role || 'Owner'}</BadgePill>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-soft-amber">
              <AlertTriangle className="w-5 h-5" />
              <span>No administrator assigned - organization can be claimed</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <Calendar className="w-6 h-6 text-electric-ice-blue mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(org.event_count)}</p>
            <p className="text-xs text-muted-text">Events</p>
          </div>
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <Users className="w-6 h-6 text-cool-mint mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(org.member_count || 0)}</p>
            <p className="text-xs text-muted-text">Members</p>
          </div>
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <QrCode className="w-6 h-6 text-soft-amber mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(org.checkin_count)}</p>
            <p className="text-xs text-muted-text">Check-ins</p>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3">
          {org.description && (
            <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
              <p className="text-sm text-muted-text mb-1">Description</p>
              <p className="text-soft-silver">{org.description}</p>
            </div>
          )}

          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <p className="text-sm text-muted-text mb-1">Created</p>
            <p className="text-cloud-white font-mono">{formatDate(org.created_at)}</p>
          </div>

          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <p className="text-sm text-muted-text mb-1">Status</p>
            <BadgePill variant={org.is_active ? 'success' : 'error'}>
              {org.is_active ? 'Active' : 'Inactive'}
            </BadgePill>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

// ==================== MAIN ORGANIZATIONS PAGE ====================
export const Organizations = () => {
  const { session } = useAuth();
  
  const [organizations, setOrganizations] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAssignPresidentOpen, setIsAssignPresidentOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pageSize: 20 });
  const [actionLoading, setActionLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.pageSize,
      });
      if (searchQuery) params.append('search', searchQuery);

      const res = await fetch(`${API_URL}/api/platform/organizations?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setOrganizations(data.organizations || []);
        setPagination(p => ({ ...p, total: data.total || 0 }));
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, pagination.page, pagination.pageSize, searchQuery]);

  // Fetch campuses for dropdown
  const fetchCampuses = useCallback(async () => {
    if (!session?.access_token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/platform/campuses?page_size=100`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampuses(data.campuses || []);
      }
    } catch (error) {
      console.error('Failed to fetch campuses:', error);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchOrganizations();
    fetchCampuses();
  }, [fetchOrganizations, fetchCampuses]);

  // Save organization (create or update)
  const handleSaveOrg = async (formData, orgId) => {
    try {
      const url = orgId 
        ? `${API_URL}/api/platform/organizations/${orgId}`
        : `${API_URL}/api/platform/organizations`;
      
      const res = await fetch(url, {
        method: orgId ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to save organization');
      }

      toast.success(orgId ? 'Organization updated' : 'Organization created');
      fetchOrganizations();
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  // Delete organization
  const handleDeleteOrg = async (orgId) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/organizations/${orgId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete organization');
      }

      toast.success('Organization deleted');
      fetchOrganizations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Deactivate organization
  const handleDeactivateOrg = async (orgId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/organizations/${orgId}/deactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Organization deactivated');
        fetchOrganizations();
      } else {
        toast.error(data.detail || 'Failed to deactivate');
      }
    } catch (error) {
      toast.error('Failed to deactivate organization');
    } finally {
      setActionLoading(false);
    }
  };

  // Reactivate organization
  const handleReactivateOrg = async (orgId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/organizations/${orgId}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Organization reactivated');
        fetchOrganizations();
      } else {
        toast.error(data.detail || 'Failed to reactivate');
      }
    } catch (error) {
      toast.error('Failed to reactivate organization');
    } finally {
      setActionLoading(false);
    }
  };

  // Search users for president assignment
  const searchUsersForPresident = async (query) => {
    if (!query || query.length < 2 || !selectedOrg) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `${API_URL}/api/platform/organizations/${selectedOrg.id}/search-users?query=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Assign president
  const handleAssignPresident = async (userId) => {
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/platform/organizations/${selectedOrg.id}/assign-president`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ user_id: userId, confirm_replace: true }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setIsAssignPresidentOpen(false);
        setUserSearch('');
        setSearchResults([]);
        fetchOrganizations();
      } else {
        toast.error(data.detail || data.message || 'Failed to assign president');
      }
    } catch (error) {
      toast.error('Failed to assign president');
    } finally {
      setActionLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Organization',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cool-mint/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-cool-mint" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.name}</p>
            <p className="text-xs text-muted-text">{row.campus_name || 'No campus'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      align: 'center',
      render: (value) => {
        const config = orgTypeConfig[value] || orgTypeConfig.organization;
        return <BadgePill variant={config.variant}>{config.label}</BadgePill>;
      },
    },
    {
      key: 'admin_email',
      label: 'Administrator',
      render: (value, row) => value ? (
        <div>
          <p className="text-cloud-white text-sm">{row.admin_name || value}</p>
          <p className="text-xs text-muted-text">{value}</p>
        </div>
      ) : (
        <span className="text-soft-amber text-sm flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Unclaimed
        </span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      align: 'center',
      render: (value) => (
        <BadgePill variant={value ? 'success' : 'error'}>
          {value ? 'Active' : 'Inactive'}
        </BadgePill>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => (
        <span className="text-soft-silver font-mono text-sm">{formatDate(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => { setSelectedOrg(row); setIsDetailOpen(true); }}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => { setEditingOrg(row); setIsFormOpen(true); }}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/[0.08]" />
            <DropdownMenuItem 
              onClick={() => { setSelectedOrg(row); setIsAssignPresidentOpen(true); }}
              className="text-amber-400 hover:bg-amber-500/10"
            >
              <Crown className="w-4 h-4 mr-2" />
              Assign President
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/[0.08]" />
            {row.is_active ? (
              <DropdownMenuItem 
                onClick={() => handleDeactivateOrg(row.id)}
                className="text-soft-amber hover:bg-soft-amber/10"
                disabled={actionLoading}
              >
                <Ban className="w-4 h-4 mr-2" />
                Deactivate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                onClick={() => handleReactivateOrg(row.id)}
                className="text-fresh-mint hover:bg-fresh-mint/10"
                disabled={actionLoading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reactivate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={() => { setSelectedOrg(row); setIsDeleteOpen(true); }}
              className="text-coral-red hover:bg-coral-red/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-cool-mint animate-spin" />
          <p className="text-soft-silver text-sm">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organizations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">Organizations</h1>
          <p className="text-soft-silver text-sm mt-1">{pagination.total} organizations on the platform</p>
        </div>
        <Button 
          onClick={() => { setEditingOrg(null); setIsFormOpen(true); }}
          className="bg-electric-ice-blue text-obsidian hover:bg-electric-ice-blue/90"
          data-testid="add-org-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Organization
        </Button>
      </div>

      {/* Search */}
      <SearchBar
        placeholder="Search organizations..."
        value={searchQuery}
        onChange={(v) => { setSearchQuery(v); setPagination(p => ({ ...p, page: 1 })); }}
      />

      {/* Table */}
      <Table
        columns={columns}
        data={organizations}
        emptyState={
          <div className="py-12">
            <EmptyState
              icon="users"
              title="No organizations yet"
              description="Add organizations for users to claim and manage."
            />
          </div>
        }
      />

      {/* Pagination */}
      {pagination.total > pagination.pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-soft-silver">
            Showing {(pagination.page - 1) * pagination.pageSize + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page * pagination.pageSize >= pagination.total}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <OrgFormDialog
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingOrg(null); }}
        org={editingOrg}
        campuses={campuses}
        onSave={handleSaveOrg}
      />

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => { setIsDeleteOpen(false); setSelectedOrg(null); }}
        org={selectedOrg}
        onConfirm={handleDeleteOrg}
      />

      <OrgDetailDrawer
        org={selectedOrg}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />

      {/* Assign President Dialog */}
      <Dialog open={isAssignPresidentOpen} onOpenChange={(open) => {
        setIsAssignPresidentOpen(open);
        if (!open) {
          setUserSearch('');
          setSearchResults([]);
        }
      }}>
        <DialogContent className="bg-graphite border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-cloud-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              Assign President
            </DialogTitle>
            <DialogDescription className="text-soft-silver">
              {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Input
                placeholder="Search users by name or email..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  searchUsersForPresident(e.target.value);
                }}
                className="bg-obsidian border-white/[0.08]"
              />
            </div>

            <div className="max-h-60 overflow-y-auto space-y-2">
              {searching ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-electric-ice-blue" />
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.08] hover:border-electric-ice-blue/30 cursor-pointer transition-colors"
                    onClick={() => handleAssignPresident(user.id)}
                  >
                    <div>
                      <p className="text-cloud-white font-medium">{user.display_name || user.email}</p>
                      <p className="text-xs text-muted-text">{user.email}</p>
                    </div>
                    {user.is_member && (
                      <span className="text-xs text-soft-silver bg-white/10 px-2 py-1 rounded">
                        {user.current_role === 'owner' ? 'President' : user.current_role?.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                ))
              ) : userSearch.length >= 2 ? (
                <p className="text-center text-muted-text py-4">No users found</p>
              ) : (
                <p className="text-center text-muted-text py-4">Type at least 2 characters to search</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignPresidentOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;
